import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MoreIcon = ({ isEnabled, onClick }) => (
    <svg
        className={isEnabled ? 'cursor-pointer text-blue-500' : 'cursor-not-allowed text-gray-400'}
        onClick={isEnabled ? onClick : undefined}
        title="Actions"
        fill="currentColor"
        viewBox="0 0 24 24"
        stroke="currentColor"
        style={{ width: '20px', height: '20px' }}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
        />
    </svg>
);

const ActionMenu = ({ menuItems, icon: Icon = MoreIcon }) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        const handleScroll = () => setOpen(false);
        const handleResize = () => setOpen(false);

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [open]);

    const updatePosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const menuWidth = 224; // w-56 = 224px
            const menuHeight = menuItems ? menuItems.length * 48 + 16 : 200; // More accurate height calculation

            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Calculate horizontal position
            let left = rect.left;

            // If menu would go off the right edge, position it to the left of the trigger
            if (left + menuWidth > viewportWidth - 16) {
                left = rect.right - menuWidth;
            }

            // Ensure menu doesn't go off the left edge
            left = Math.max(16, left);

            // Calculate vertical position
            let top = rect.bottom + 8; // Default: below the icon with 8px gap

            // Check if there's enough space below
            const spaceBelow = viewportHeight - rect.bottom;

            // If not enough space below, position just above the icon
            if (spaceBelow < menuHeight + 16) {
                // 16px for padding
                top = rect.top - 8; // Just above the icon with 8px gap
                // Transform will make menu appear above this point
            }

            // Final safety check - if menu would go off top, adjust
            if (top - menuHeight < 16) {
                top = Math.max(16 + menuHeight, rect.bottom + 8);
            }

            setPosition({ top, left });
        }
    };

    const handleToggle = (e) => {
        e.stopPropagation();
        if (menuItems && menuItems.length > 0) {
            if (!open) {
                updatePosition();
            }
            setOpen((v) => !v);
        }
    };

    // Update position when menu opens or when window resizes/scrolls
    useEffect(() => {
        if (open) {
            updatePosition();
        }
    }, [open, menuItems]);

    // Determine if menu should appear above based on available space
    const shouldAppearAbove = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const menuHeight = menuItems ? menuItems.length * 48 + 16 : 200;
            const spaceBelow = window.innerHeight - rect.bottom;
            return spaceBelow < menuHeight + 16;
        }
        return false;
    };

    const dropdown = open && (
        <div
            ref={menuRef}
            className="fixed z-50 w-56 bg-white border border-gray-200 rounded shadow-lg flex flex-col"
            style={{
                top: position.top,
                left: position.left,
                transform: shouldAppearAbove() ? 'translateY(-100%)' : 'none',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {menuItems.map((item, idx) => (
                <button
                    key={idx}
                    className={`flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-100 text-[#5b6b8b] text-sm font-normal ${
                        item.enabled === false ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    disabled={item.enabled === false}
                    onClick={
                        item.enabled === false
                            ? undefined
                            : () => {
                                  item.onClick();
                                  setOpen(false);
                              }
                    }
                    type="button"
                >
                    {item.icon}
                    {item.label}
                </button>
            ))}
        </div>
    );

    return (
        <>
            <span ref={triggerRef} onClick={handleToggle}>
                <Icon isEnabled={menuItems && menuItems.length > 0} />
            </span>
            {dropdown && createPortal(dropdown, document.body)}
        </>
    );
};

export default ActionMenu;
